// THE FIRST THREAD of a personalized sign-up (POB S7) — the Fly start
// screen's last part for the person who signed up with their website read
// (functions/assistantFlyResearch.js tells who, what it reads and from where;
// decision 11 of 2026-10-25). Three parts, in order:
//
//   AssistantFlyResearchSummary  what was read and from where — a line per
//                                fact with its page as a link (and, with the
//                                next SDK, its own delete); Review what I
//                                read, with Done to confirm it; Delete all
//                                research (every delete: confirm first)
//   AssistantFlyOffers           Website · Team · Imports (AssistantFlyOfferCard):
//                                nothing acts on its own; an offer whose read
//                                or sheet is not there yet says "Soon"
//   AssistantFlyChecklist        Getting started (AssistantFlyChecklistItem):
//                                the server's ticks only; one line on a phone
//
// The block holds its reads in its OWN state (every key declared, so no read
// falls back to a root key of the same name — and the checklist never reads
// a flag another surface set). It asks when the start screen shows, when it
// comes back, the workspace changes or the person's record arrives
// (stateDeps), and when the window gets the focus back (the checklist's
// "re-read when focus returns"). It appears once its first read is in,
// whole, as the screen's last part: it moves nothing above it (Rule 66).
// Kept mounted, only hidden.
export const AssistantFlyResearch = {
  tag: 'section',
  dataFlyResearch: true,
  state: {
    pobWs: '',
    pobReadAt: 0,
    pobResearch: null,
    pobPeopleOk: false,
    pobPeople: [],
    pobSourcesOk: false,
    pobSources: [],
    pobReviewOpen: false,
    pobDeleting: false,
    pobDeleted: null,
    pobDeleteFailed: false,
    pobChecklistOpen: false,
    // the board's checklist as the block last read it; what this SDK can do
    pobBoardChecklist: null,
    pobCanReview: false,
    pobCanDeleteLine: false,
    // Done on the review; one line's delete
    pobReviewing: false,
    pobReviewFailed: false,
    pobDeletingLine: '',
    pobLineDeleted: ''
  },
  hide: (el, s) => !el.call('assistantFlyResearchShown', s.root, s),
  flow: 'y',
  gap: 'B',
  minWidth: '0',
  onRender: (el, s) => el.call('assistantFlyResearchPrime', el, s),
  stateDeps: [
    (el, s) => !!el.call('assistantFlyStartShown', s.root),
    (el, s) => !!(s.root && s.root.workspaceReady),
    (el, s) =>
      s.root && s.root.workspace ? String(s.root.workspace.id || s.root.workspace._id || '') : '',
    (el, s) => (s.root && s.root._pobBuild ? s.root._pobBuild.status : ''),
    // the person's record: it arrives after the first paint (the preferences
    // load late) or changes its kind — POB or Fly, the board is asked then
    (el, s) => {
      const p = s.root && s.root.userPrefs
      const ob = p && p.onboarding && typeof p.onboarding === 'object' ? p.onboarding : null
      return ob ? String(ob.via || '') + '/' + String(ob.origin || '') : ''
    }
  ],
  // the screen opened again, the workspace changed or the record arrived:
  // the reads again
  onStateUpdate: (el, s) => el.call('assistantFlyResearchPrime', el, s, { force: true }),
  // the focus came back (an invite sent, an import run in another tab): the
  // checklist is the server's, so it is read again
  onWindowFocus: (e, el, s) => el.call('assistantFlyResearchPrime', el, s, { force: true }),

  AssistantFlyResearchSummary: {},
  AssistantFlyOffers: {},
  AssistantFlyChecklist: {}
}

// WHAT I READ — the lead, a line per fact with its page, the pages read (in
// place, on demand) with Done under them, and the two actions; after a
// delete, what went.
export const AssistantFlyResearchSummary = {
  tag: 'section',
  dataFlyResearchSummary: true,
  flow: 'y',
  gap: 'Z',
  minWidth: '0',
  boxSizing: 'border-box',
  padding: 'B',
  round: 'radiusCard',
  background: (el) =>
    el.call('assistantFlyOnHome', el)
      ? 'var(--home-card-bg, var(--theme-card-background))'
      : 'cardSolid',
  '@mobileL': { padding: 'A' },
  hide: (el, s) => !el.call('assistantFlyResearchView', s.root, s).show,

  ResearchLead: {
    tag: 'p',
    margin: '0',
    fontSize: 'A',
    fontWeight: '500',
    lineHeight: '1.4',
    color: 'title',
    overflowWrap: 'anywhere',
    hide: (el, s) => !el.call('assistantFlyResearchView', s.root, s).lead,
    text: (el, s) => el.call('assistantFlyResearchView', s.root, s).lead
  },

  ResearchLines: {
    tag: 'ul',
    margin: '0',
    padding: '0',
    listStyle: 'none',
    flow: 'y',
    gap: 'Y',
    minWidth: '0',
    hide: (el, s) => !el.call('assistantFlyResearchView', s.root, s).lines.length,
    children: (el, s) => el.call('assistantFlyResearchView', s.root, s).lines,
    childrenAs: 'state',
    childExtends: 'AssistantFlyResearchLine'
  },

  // Review what I read — the pages, in place
  ResearchPages: {
    tag: 'ul',
    dataFlyResearchPages: true,
    margin: '0',
    padding: '0',
    listStyle: 'none',
    flow: 'y',
    gap: 'Y',
    minWidth: '0',
    hide: (el, s) => {
      const v = el.call('assistantFlyResearchView', s.root, s)
      return !v.reviewOpen || !v.pages.length
    },
    children: (el, s) => el.call('assistantFlyResearchView', s.root, s).pages,
    childrenAs: 'state',
    childExtends: 'AssistantFlyResearchLine'
  },

  // Done — the owner read the pages: the server ticks "Review your profile"
  // (owners and admins, with an SDK that has the method)
  ResearchReviewDone: {
    flow: 'x',
    align: 'center',
    minWidth: '0',
    hide: (el, s) => !el.call('assistantFlyResearchView', s.root, s).reviewDoneShown,
    AppCapsuleBtnPrimary: {
      dataFlyResearchAction: 'reviewed',
      compact: true,
      disabled: (el, s) => !!el.call('assistantFlyResearchView', s.root, s).reviewing,
      text: (el, s) => el.call('assistantFlyResearchView', s.root, s).reviewDoneText,
      onClick: (e, el) => {
        e.stopPropagation()
        el.call('assistantFlyResearchReviewDone', el)
      }
    }
  },

  ResearchDone: {
    tag: 'p',
    role: 'status',
    margin: '0',
    fontSize: 'Z2',
    lineHeight: '1.5',
    color: 'caption',
    hide: (el, s) => !el.call('assistantFlyResearchView', s.root, s).deletedText,
    text: (el, s) => el.call('assistantFlyResearchView', s.root, s).deletedText
  },

  ResearchFailed: {
    tag: 'p',
    role: 'alert',
    margin: '0',
    fontSize: 'Z2',
    lineHeight: '1.5',
    color: 'dangerInk',
    hide: (el, s) => !el.call('assistantFlyResearchView', s.root, s).failedText,
    text: (el, s) => el.call('assistantFlyResearchView', s.root, s).failedText
  },

  ResearchActions: {
    flow: 'x',
    align: 'center',
    flexWrap: 'wrap',
    gap: 'Z',
    minWidth: '0',
    hide: (el, s) => !el.call('assistantFlyResearchView', s.root, s).lines.length,
    AppCapsuleBtnToolbar: {
      dataFlyResearchAction: 'review',
      compact: true,
      ariaExpanded: (el, s) =>
        el.call('assistantFlyResearchView', s.root, s).reviewOpen ? 'true' : 'false',
      hide: (el, s) => !el.call('assistantFlyResearchView', s.root, s).pages.length,
      text: (el, s) => el.call('assistantFlyResearchView', s.root, s).reviewText,
      onClick: (e, el, s) => {
        e.stopPropagation()
        s.update({ pobReviewOpen: s.pobReviewOpen !== true })
      }
    },
    // destructive: the kit's danger capsule (the confirm comes first)
    AppCapsuleBtnDanger: {
      dataFlyResearchAction: 'delete',
      compact: true,
      hide: (el, s) => !el.call('assistantFlyResearchView', s.root, s).deleteShown,
      disabled: (el, s) => !!el.call('assistantFlyResearchView', s.root, s).deleting,
      text: '{{ workspace.pobThread.delete | polyglot }}',
      onClick: (e, el) => {
        e.stopPropagation()
        el.call('assistantFlyResearchDelete', el)
      }
    }
  }
}

// One fact of the summary (or one page read): the words, then where they came
// from — a link to the page, in a new tab (the workspace stays). A fact the
// server can delete alone has its own delete at the row's end (owners and
// admins, with the next SDK; the confirm comes first); while one row has it,
// every fact row keeps its height, so the rows stay even.
export const AssistantFlyResearchLine = {
  tag: 'li',
  flow: 'x',
  align: 'baseline',
  gap: 'Z',
  minWidth: '0',
  isActionRow: (el, s) => !!(s && s.actionRow === true),
  '.isActionRow': { alignItems: 'center', minHeight: 'controlHCompact' },
  LineBody: {
    flow: 'x',
    align: 'baseline',
    flexWrap: 'wrap',
    gap: 'X Z',
    flex: '1 1 auto',
    minWidth: '0',
    LineText: {
      tag: 'span',
      minWidth: '0',
      fontSize: 'Z2',
      lineHeight: '1.5',
      color: 'title',
      overflowWrap: 'anywhere',
      text: (el, s) => (s && s.text) || ''
    },
    Link: {
      flow: 'x',
      align: 'center',
      gap: 'X',
      minWidth: '0',
      fontSize: 'Z',
      lineHeight: '1.5',
      color: 'caption',
      textDecoration: 'none',
      target: '_blank',
      rel: 'noopener',
      ':hover': { color: 'title', textDecoration: 'underline' },
      ':active': { color: 'title' },
      hide: (el, s) => !(s && s.href),
      href: (el, s) => (s && s.href) || null,
      LinkText: {
        tag: 'span',
        overflowWrap: 'anywhere',
        text: (el, s) => (s && s.linkText) || ''
      },
      Icon: { ariaHidden: 'true', name: 'arrowUpRight', boxSize: 'iconXs', flexShrink: '0' }
    }
  },
  // this one fact only — the kit's quiet icon square (the confirm says what)
  CompactIconButton: {
    dataFlyResearchAction: 'deleteLine',
    icon: 'trash',
    hide: (el, s) => !(s && s.deleteShown === true),
    disabled: (el, s) => !!(s && s.deleting === true),
    ariaLabel: (el, s) => (s && s.deleteLabel) || null,
    title: (el, s) => (s && s.deleteLabel) || null,
    onClick: (e, el, s) => {
      e.stopPropagation()
      el.call('assistantFlyResearchDeleteLine', el, s && s.deleteLine, s && s.text)
    }
  }
}

// THE OFFERS — one line, then the three cards on one row (a column on a
// phone). Nothing acts on its own.
export const AssistantFlyOffers = {
  tag: 'section',
  dataFlyOffers: true,
  flow: 'y',
  gap: 'Z',
  minWidth: '0',
  hide: (el, s) => !el.call('assistantFlyOffersView', s.root, s).show,
  OffersLead: {
    tag: 'p',
    margin: '0',
    fontSize: 'Z2',
    lineHeight: '1.5',
    color: 'caption',
    text: (el, s) => el.call('assistantFlyOffersView', s.root, s).lead
  },
  OffersRow: {
    role: 'list',
    display: 'grid',
    gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
    gridAutoRows: '1fr',
    gap: 'Z',
    minWidth: '0',
    '@mobileL': { gridTemplateColumns: 'minmax(0, 1fr)' },
    children: (el, s) => el.call('assistantFlyOffersView', s.root, s).rows,
    childrenAs: 'state',
    childExtends: 'AssistantFlyOfferCard'
  }
}

// ONE OFFER — its glyph and name, one line of what is ready, then its one
// action — or "Soon" when its read or its sheet is not there yet (no dead
// button). The card's row carries every key it reads.
export const AssistantFlyOfferCard = {
  role: 'listitem',
  dataFlyOffer: (el, s) => (s && s.offer) || null,
  dataSoon: (el, s) => (s && s.soon === true ? 'true' : null),
  flow: 'y',
  justifyContent: 'space-between',
  gap: 'A',
  minWidth: '0',
  boxSizing: 'border-box',
  padding: 'A',
  round: 'radiusCard',
  background: (el) =>
    el.call('assistantFlyOnHome', el)
      ? 'var(--home-card-bg, var(--theme-card-background))'
      : 'cardSolid',
  color: 'title',
  OfferHead: {
    flow: 'y',
    gap: 'Y',
    minWidth: '0',
    OfferTitle: {
      flow: 'x',
      align: 'center',
      gap: 'Y',
      minWidth: '0',
      Icon: {
        ariaHidden: 'true',
        name: (el, s) => (s && s.icon) || 'magicstar',
        boxSize: 'iconSm',
        flexShrink: '0',
        color: 'caption'
      },
      OfferName: {
        tag: 'span',
        minWidth: '0',
        fontSize: 'A',
        fontWeight: '500',
        lineHeight: '1.3',
        overflowWrap: 'anywhere',
        text: (el, s) => (s && s.title) || ''
      }
    },
    OfferLead: {
      tag: 'span',
      minWidth: '0',
      fontSize: 'Z2',
      lineHeight: '1.45',
      color: 'caption',
      overflowWrap: 'anywhere',
      text: (el, s) => (s && s.lead) || ''
    }
  },
  OfferFoot: {
    flow: 'x',
    align: 'center',
    minWidth: '0',
    // not there yet: the kit's status cell, the label carries the meaning.
    // 'neutral', never null: a nullish status falls through to the row's
    // `s.status` inside AppStatusChip (pages/admin/analytics.js, the same)
    AppStatusChip: {
      status: 'neutral',
      label: 'workspace.pobThread.soon',
      hide: (el, s) => !s || s.soon !== true
    },
    AppCapsuleBtnToolbar: {
      dataFlyOfferAction: (el, s) => (s && s.offer) || null,
      compact: true,
      hide: (el, s) => !s || s.soon === true || !s.href,
      text: (el, s) => (s && s.actionText) || '',
      onClick: (e, el, s) => {
        e.stopPropagation()
        el.call('assistantFlyOfferOpen', s, el)
      }
    }
  }
}

// GETTING STARTED — the title with its count, then the four items; on a phone
// one line ("0 of 4 done") that opens the items.
export const AssistantFlyChecklist = {
  tag: 'section',
  dataFlyChecklist: true,
  flow: 'y',
  gap: 'Z',
  minWidth: '0',
  hide: (el, s) => !el.call('assistantFlyChecklistView', s.root, s).show,
  ChecklistHead: {
    flow: 'x',
    align: 'center space-between',
    gap: 'A',
    minWidth: '0',
    hide: (el, s) => el.call('assistantFlyChecklistView', s.root, s).folded,
    AssistantFlyStartPartTitle: {
      text: (el, s) => el.call('assistantFlyChecklistView', s.root, s).title
    },
    ChecklistCount: {
      tag: 'span',
      fontSize: 'Z',
      color: 'caption',
      fontVariantNumeric: 'tabular-nums',
      text: (el, s) => el.call('assistantFlyChecklistView', s.root, s).progressText
    }
  },
  // the phone's one line: the count, and the items on demand (function text
  // on its own span beside the chevron — a `{{ }}` label on the button would
  // drop the Icon on a language switch)
  AppCapsuleBtnGhost: {
    dataFlyChecklistFold: true,
    compact: true,
    alignSelf: 'flex-start',
    ariaExpanded: 'false',
    hide: (el, s) => !el.call('assistantFlyChecklistView', s.root, s).folded,
    onClick: (e, el, s) => {
      e.stopPropagation()
      s.update({ pobChecklistOpen: true })
    },
    FoldText: {
      tag: 'span',
      text: (el, s) => el.call('assistantFlyChecklistView', s.root, s).foldText
    },
    Icon: { ariaHidden: 'true', name: 'chevronDown', boxSize: 'iconXs', flexShrink: '0' }
  },
  ChecklistItems: {
    tag: 'ul',
    margin: '0',
    padding: '0',
    listStyle: 'none',
    display: 'flex',
    flexWrap: 'wrap',
    gap: 'Y A',
    minWidth: '0',
    hide: (el, s) => el.call('assistantFlyChecklistView', s.root, s).folded,
    children: (el, s) => el.call('assistantFlyChecklistView', s.root, s).rows,
    childrenAs: 'state',
    childExtends: 'AssistantFlyChecklistItem'
  }
}

// ONE ITEM — the tick (or an empty ring) and the item's name; the mark says
// its state in words to assistive tech. Not a control: the offers above are
// the actions.
export const AssistantFlyChecklistItem = {
  tag: 'li',
  dataChecked: (el, s) => (s && s.ticked === true ? 'true' : null),
  flow: 'x',
  align: 'center',
  gap: 'Y',
  minWidth: '0',
  fontSize: 'Z2',
  color: (el, s) => (s && s.ticked === true ? 'title' : 'caption'),
  ItemMark: {
    role: 'img',
    ariaLabel: (el, s) => (s && s.stateText) || null,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: '0',
    boxSize: 'iconSm',
    round: 'radiusPill',
    border: (el, s) => (s && s.ticked === true ? 'none' : 'hairline solid line'),
    Icon: {
      ariaHidden: 'true',
      hide: (el, s) => !s || s.ticked !== true,
      name: 'checkmark',
      boxSize: 'iconXs'
    }
  },
  ItemName: {
    tag: 'span',
    minWidth: '0',
    overflowWrap: 'anywhere',
    text: (el, s) => (s && s.title) || ''
  }
}
